import { useCallback, useEffect, useRef, useState } from 'react';
import type { Inquiry, PendingClarification } from '@/types/chat/chat';
import { runStore } from '../../runs/runStore';
import type { ConvMessage } from '../../types';

/**
 * The longest a run's ending holds the view: a hung read must not hide a
 * clarification that is pending again.
 */
export const SETTLE_LIMIT_MS = 5_000;

interface Options {
  inquiryId: string | undefined;
  /** The open Q&A as last read: the server's word on what is pending. */
  inquiry: Inquiry | undefined;
  /** The organization has clarification on: the extra reads below run. */
  enabled: boolean;
  /** Recent Q&As says this Q&A waits for a reply. */
  listedNeedsReply: boolean;
  messages: ConvMessage[];
  /** A run is active (or being sent) in this Q&A. */
  busy: boolean;
  /** Read the inquiry again; resolves to whether the answer was applied. */
  refresh: (inquiryId: string) => Promise<boolean>;
  reloadMessages: () => Promise<void>;
  onPending?: (inquiryId: string, pending: boolean) => void;
}

/**
 * Whether a clarification waits for this user's reply in the open Q&A. The
 * inquiry read decides (it is what survives a reload and a second device);
 * this keeps that read current wherever the state can change without this
 * tab doing anything: a run ending, another tab or device, a read that failed.
 */
export const useClarificationState = ({
  inquiryId,
  inquiry,
  enabled,
  listedNeedsReply,
  messages,
  busy,
  refresh,
  reloadMessages,
  onPending,
}: Options) => {
  const pending: PendingClarification | null =
    inquiry?.pending_clarification ?? null;
  const hasPending = !!pending;
  const loadedId = inquiry ? String(inquiry.id) : null;
  const openRow =
    messages.find(item => item.clarification?.status === 'awaiting_reply')
      ?.id ?? null;

  const latest = useRef({ refresh, reloadMessages });
  latest.current = { refresh, reloadMessages };
  const pendingMessageId = pending?.message_id ?? null;
  const now = { loadedId, hasPending, pendingMessageId, openRow, busy };
  const shown = useRef(now);
  shown.current = now;

  /**
   * A run ended with another message's clarification on screen: what was read
   * while it ran is out of date, so nothing waits until both reads land.
   * Without this, the status and Skip came back for a moment after the reply
   * was answered. Held per Q&A, so opening another one is not affected, and by
   * token, so the newest ending decides.
   */
  const [settlingFor, setSettlingFor] = useState<string | null>(null);
  const settling = settlingFor !== null && settlingFor === inquiryId;
  const settleToken = useRef(0);
  /** A run's ending is being read, so a badge it sets needs no read of its own. */
  const readingEnding = useRef(0);

  /** One retry: a lost read would hide the state until the next change. */
  const read = useCallback(async (id: string) => {
    if (!(await latest.current.refresh(id))) await latest.current.refresh(id);
  }, []);

  // Every applied read tells Recent Q&As, also one that confirms the value:
  // the badge may have been set meanwhile by a list read of another moment.
  useEffect(() => {
    if (loadedId) onPending?.(loadedId, hasPending);
  }, [hasPending, inquiry, loadedId, onPending]);

  // The list and this Q&A disagree the other way: either a list read from
  // before a run asked, or the clarification closed elsewhere. A read decides.
  // While a run is active the list is right: the reply has claimed it.
  useEffect(() => {
    if (!loadedId || !hasPending || listedNeedsReply) return;
    if (shown.current.busy) return;
    void read(loadedId);
  }, [hasPending, listedNeedsReply, loadedId, read]);

  // A run of this Q&A ended: it may have asked, or closed what was pending.
  // A run that asked proves the flag is on, and something pending needs
  // reading whatever a failed /config read says.
  useEffect(
    () =>
      runStore.onFinished(run => {
        if (run.inquiryId !== inquiryId) return;
        const state = shown.current;
        const asked = run.phase === 'awaiting_reply';
        if (!enabled && !state.hasPending && !asked) return;

        readingEnding.current += 1;
        const reads: Promise<unknown>[] = [read(run.inquiryId)];
        const otherClarification = [state.pendingMessageId, state.openRow].some(
          id => id !== null && Number(id) !== run.messageId,
        );
        if (otherClarification) {
          reads.push(latest.current.reloadMessages());
          settleToken.current += 1;
          const token = settleToken.current;
          setSettlingFor(run.inquiryId);
          void Promise.race([
            Promise.allSettled(reads),
            new Promise(resolve => setTimeout(resolve, SETTLE_LIMIT_MS)),
          ]).then(() => {
            if (settleToken.current === token) setSettlingFor(null);
          });
        }
        void Promise.allSettled(reads).then(() => {
          readingEnding.current -= 1;
        });
      }),
    [enabled, inquiryId, read],
  );

  // The list shows an open clarification the inquiry read does not know of
  // (it was read first, or a run picked up after a reload had already asked).
  // Once per row: a colleague's stays open and is not pending for this user.
  const readForRow = useRef<ConvMessage['id'] | null>(null);
  useEffect(() => {
    if (!enabled || !loadedId || hasPending || openRow === null) return;
    if (readForRow.current === openRow) return;
    readForRow.current = openRow;
    void read(loadedId);
  }, [enabled, hasPending, loadedId, openRow, read]);

  // Recent Q&As comes to know of one that this conversation does not: asked
  // elsewhere. On that change only, not whenever the two differ for a moment.
  useEffect(() => {
    if (!enabled || !loadedId || !listedNeedsReply) return;
    if (shown.current.hasPending || readingEnding.current > 0) return;
    void read(loadedId);
    void latest.current.reloadMessages();
  }, [enabled, listedNeedsReply, loadedId, read]);

  // Back to the tab: it may have been answered or closed elsewhere.
  useEffect(() => {
    if (!enabled) return undefined;
    const onFocus = () => {
      const state = shown.current;
      if (state.loadedId && (state.hasPending || state.openRow !== null)) {
        void read(state.loadedId);
        void latest.current.reloadMessages();
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [enabled, read]);

  /** Read both again, when an action found it was already closed. */
  const resync = useCallback(() => {
    if (shown.current.loadedId) void read(shown.current.loadedId);
    void latest.current.reloadMessages();
  }, [read]);

  return {
    /**
     * Waiting for the user: not while a run is active, which is the reply
     * being answered, nor until its ending has been read (the badge keeps
     * the server's value meanwhile).
     */
    waitingMessageId: pending && !busy && !settling ? pending.message_id : null,
    replying: hasPending && !busy && !settling,
    settling,
    resync,
  };
};
